import { modalCloseButton } from 'assets/jss/material-dashboard-pro-react';
import { Field, Form, Formik } from 'formik';
import React from 'react';
import { toast } from 'sonner';
import * as Yup from 'yup';

import { ButtonBase, Dialog } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';

import Button from 'creative-components/CustomButtons/Button';
import CustomInput from 'creative-components/CustomInput/CustomInput';
import GridContainer from 'creative-components/Grid/GridContainer';
import GridItem from 'creative-components/Grid/GridItem';

import CustomSelect from 'components/CustomSelect/CustomSelect';
import { useLoadingIndicatorContext } from 'components/LoadingIndicator/LoadingIndicatorProvider';

import { submitAgentLandingSellForThisPriceForm } from 'utils/api';
import { isAgentLandingPageSample, phoneRegExp } from 'utils/lib';

const useStyles = makeStyles((theme) => ({
  modalCloseButton,
  title: {
    fontSize: '24px',
    lineHeight: '28px',
    color: theme.palette.darkGray.main,
    margin: '0 0 16px',
  },
  subtitle: {
    fontSize: '14px',
    lineHeight: '20px',
    color: theme.palette.darkGray.main,
    margin: '0 0 32px',
  },
  button: {
    margin: '32px 0 0',
    color: 'black',
    width: '100%',
  },
}));

const howSoonToSellOptions = ['in 1-2 months', 'in 3-4 months', 'in 5-6 months', 'in 6+ months'];

const SellForThisPriceForm = ({ onClose, agentLandingPageToken }) => {
  const classes = useStyles();
  const { showLoadingIndicatorModal, hideLoadingIndicatorModal } = useLoadingIndicatorContext();

  const formRef = React.useRef();

  return (
    <Dialog
      open
      keepMounted
      maxWidth="xs"
      scroll="body"
      onClose={onClose}
    >
      <ButtonBase
        className={classes.modalCloseButton}
        aria-label="Close"
        onClick={onClose}
      >
        <CloseIcon />
      </ButtonBase>
      <Formik
        innerRef={formRef}
        initialValues={{
          phoneNumber: '',
          email: '',
          howSoonToSell: '',
          wantProfessionalOpinion: '',
          recaptchaValue: '',
        }}
        onSubmit={async ({
          phoneNumber, email, howSoonToSell, wantProfessionalOpinion, recaptchaValue,
        }) => {
          showLoadingIndicatorModal();

          try {
            if (!isAgentLandingPageSample(agentLandingPageToken)) {
              await submitAgentLandingSellForThisPriceForm(agentLandingPageToken, phoneNumber, email, howSoonToSell, wantProfessionalOpinion, recaptchaValue);
            }

            toast.success('Thank you for your request, I will be in touch!');

            onClose();
          } catch (error) {
            console.error('error', error);
          }

          hideLoadingIndicatorModal();
        }}
        validationSchema={Yup.object().shape({
          phoneNumber: Yup.string().required().matches(phoneRegExp, 'Phone number is not valid'),
          email: Yup.string().email().required(),
          howSoonToSell: Yup.string().required(),
          wantProfessionalOpinion: Yup.boolean().required(),
          recaptchaValue: Yup.string(), // .required(),
        })}
      >
        {(props) => {
          const {
            touched, errors, dirty, isValid, handleSubmit, isSubmitting, setFieldValue, values,
          } = props;

          return (
            <Form>
              <GridContainer>
                <GridItem xs={12}>
                  <h3 className={classes.title}>Would you sell for this price?</h3>
                  <p className={classes.subtitle}>If you think you'd be up for considering selling your home for this price, we're happy to help.</p>
                </GridItem>
                <GridItem xs={12}>
                  <Field name="phoneNumber">
                    {({ field }) => (
                      <CustomInput
                        labelText="Phone Number"
                        labelBold
                        labelRequired
                        inputProps={{
                          ...field,
                          type: 'tel',
                          autoComplete: 'tel',
                        }}
                        mask={['(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                        error={touched[field.name] && errors[field.name] !== undefined}
                      />
                    )}
                  </Field>
                </GridItem>
                <GridItem xs={12}>
                  <Field name="email">
                    {({ field }) => (
                      <CustomInput
                        labelText="Email Address"
                        labelBold
                        labelRequired
                        inputProps={{
                          ...field,
                          autoComplete: 'email',
                        }}
                        error={touched[field.name] && errors[field.name] !== undefined}
                      />
                    )}
                  </Field>
                </GridItem>
                <GridItem xs={12}>
                  <Field name="howSoonToSell">
                    {({ field }) => (
                      <CustomSelect
                        labelText="How soon do you want to sell?"
                        labelBold
                        labelRequired
                        items={howSoonToSellOptions.map((amount) => ({ value: amount, name: amount }))}
                        inputProps={{
                          ...field,
                        }}
                        error={touched[field.name] && errors[field.name] !== undefined}
                      />
                    )}
                  </Field>
                </GridItem>
                <GridItem xs={12}>
                  <Field name="wantProfessionalOpinion">
                    {({ field }) => (
                      <CustomSelect
                        labelText="I want your professional opinion"
                        labelBold
                        labelRequired
                        items={[{ value: true, name: 'Yes' }, { value: false, name: 'No' }]}
                        inputProps={{
                          ...field,
                        }}
                        error={touched[field.name] && errors[field.name] !== undefined}
                      />
                    )}
                  </Field>
                </GridItem>
                {/* <GridItem xs={12}>
                  <Field name="recaptchaValue">
                    {({ field }) => (
                      <ReCAPTCHAInput
                        fieldName={field.name}
                      />
                    )}
                  </Field>
                </GridItem> */}
                <GridItem xs={12}>
                  <Button
                    color="primary"
                    round
                    outlined
                    size="lg"
                    className={classes.button}
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    Let us know
                  </Button>
                </GridItem>
              </GridContainer>
            </Form>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default SellForThisPriceForm;
